import React from 'react';
import { PageHero, BottomCTA } from '../components/PageHelpers';
import { Sec, SH } from '../components/Section';
import { Btn, Crd } from '../components/Button';
import { C } from '../constants';
import { useTheme } from '../contexts/ThemeContext';
import { motion } from 'framer-motion';
import { supabase } from '../lib/supabase';

// About Page - Exact original content
export const AboutPage = () => {
  const { theme } = useTheme();

  return (
    <div data-testid="about-page">
      <PageHero
        eyebrow="ABOUT DECX AI"
        title="The Story Behind"
        accent="DecX AI"
        subtitle="Built by operators, for operators."
        body="Founded by Neeraj P with 18+ years of building, advising, and investing. After seeing thousands of decks, DecX AI was born to solve fragmented deck intelligence."
      />

      <Sec dark={theme === 'light'}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <SH
            eyebrow="OUR MISSION"
            title="Transform Every Deck"
            accent="Into a Closing Machine"
            center
          />

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            style={{ marginTop: '40px' }}
          >
            <Crd glow style={{ textAlign: 'center' }}>
              <p className="font-[Manrope]" style={{ fontSize: '18px', lineHeight: 1.8, opacity: 0.85 }}>
                Every founder, sales team, and PM deserves the intelligence to tell their story right. DecX AI makes that possible.
              </p>
            </Crd>
          </motion.div>
        </div>
      </Sec>

      <BottomCTA
        title="Ready to Transform Your Deck"
        accent="to DecX?"
        subtitle="Start free. Get your score. Close your deal."
        ctaText="Get Free DecX Score"
        ctaLink="/score"
      />
    </div>
  );
};

// Contact Page - Updated with Supabase integration and rate limiting
export const ContactPage = () => {
  const { theme } = useTheme();
  const [formData, setFormData] = React.useState({ 
    name: '', 
    email: '', 
    company: '', 
    message: '' 
  });
  const [submitted, setSubmitted] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [rateLimitError, setRateLimitError] = React.useState(false);
  const [remainingTime, setRemainingTime] = React.useState(0);

  // Load submission history from localStorage
  const getSubmissionHistory = () => {
    const history = localStorage.getItem('contact_submissions');
    if (!history) return [];
    try {
      return JSON.parse(history);
    } catch {
      return [];
    }
  };

  // Save submission to localStorage
  const saveSubmission = () => {
    const history = getSubmissionHistory();
    const now = Date.now();
    history.push(now);
    // Keep only last 10 submissions to save space
    const recentHistory = history.filter(timestamp => now - timestamp < 5 * 60 * 1000);
    localStorage.setItem('contact_submissions', JSON.stringify(recentHistory));
    return recentHistory;
  };

  // Check if rate limit is exceeded
  const checkRateLimit = () => {
    const history = getSubmissionHistory();
    const now = Date.now();
    const recentSubmissions = history.filter(timestamp => now - timestamp < 5 * 60 * 1000);
    
    if (recentSubmissions.length >= 3) {
      const oldestSubmission = Math.min(...recentSubmissions);
      const timeToWait = 5 * 60 * 1000 - (now - oldestSubmission);
      const minutesLeft = Math.ceil(timeToWait / 60000);
      setRemainingTime(minutesLeft);
      return false;
    }
    return true;
  };

  // Clear rate limit error after time
  React.useEffect(() => {
    let interval;
    if (rateLimitError && remainingTime > 0) {
      interval = setInterval(() => {
        setRemainingTime(prev => {
          if (prev <= 1) {
            setRateLimitError(false);
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 60000);
    }
    return () => clearInterval(interval);
  }, [rateLimitError, remainingTime]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check rate limit first
    if (!checkRateLimit()) {
      setRateLimitError(true);
      setError(`You've reached the maximum of 3 submissions. Please wait ${remainingTime} minute(s) before trying again.`);
      return;
    }

    setLoading(true);
    setError('');
    setRateLimitError(false);

    try {
      // Prepare the data for insertion
      const contactData = {
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        company: formData.company?.trim() || null,
        message: formData.message.trim(),
        status: 'unread',
        created_at: new Date().toISOString()
      };

      // Insert into Supabase contacts table
      const { data, error: supabaseError } = await supabase
        .from('contacts')
        .insert([contactData])
        .select();

      if (supabaseError) {
        console.error('Supabase error:', supabaseError);
        
        // Handle specific Supabase errors
        if (supabaseError.code === '23505') {
          throw new Error('Duplicate submission detected. Please wait a moment.');
        } else if (supabaseError.code === '42P01') {
          throw new Error('Database configuration error. Please contact support.');
        } else {
          throw new Error(supabaseError.message);
        }
      }

      // Save successful submission to localStorage for rate limiting
      saveSubmission();
      
      console.log('Contact saved successfully:', data);
      setSubmitted(true);
      
      // Reset form after successful submission
      setFormData({ name: '', email: '', company: '', message: '' });
      
    } catch (err) {
      console.error('Error submitting contact:', err);
      setError(err.message || 'Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const inputStyle = {
    width: '100%',
    padding: '14px 18px',
    borderRadius: '12px',
    border: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'}`,
    background: theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
    color: theme === 'dark' ? C.white : C.black,
    fontSize: '16px',
    fontFamily: "'Manrope', sans-serif",
    outline: 'none',
    transition: 'all 0.3s ease'
  };

  const buttonStyle = {
    width: '100%',
    justifyContent: 'center',
    opacity: loading ? 0.7 : 1,
    cursor: loading ? 'not-allowed' : 'pointer'
  };

  return (
    <div data-testid="contact-page">
      <PageHero
        eyebrow="CONTACT US"
        title="Let's Talk"
        accent="Decks"
        subtitle="Questions, enterprise inquiries, or just say hello."
      />

      <Sec dark={theme === 'light'}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '48px' }}>
            <div>
              {!submitted ? (
                <Crd glow data-testid="contact-form-card">
                  <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    <input
                      type="text"
                      name="name"
                      placeholder="Your Name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      disabled={loading}
                      style={inputStyle}
                      data-testid="contact-name-input"
                    />
                    <input
                      type="email"
                      name="email"
                      placeholder="Work Email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      disabled={loading}
                      style={inputStyle}
                      data-testid="contact-email-input"
                    />
                    <input
                      type="text"
                      name="company"
                      placeholder="Company"
                      value={formData.company}
                      onChange={handleChange}
                      disabled={loading}
                      style={inputStyle}
                      data-testid="contact-company-input"
                    />
                    <textarea
                      name="message"
                      placeholder="How can we help?"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      disabled={loading}
                      style={{ ...inputStyle, resize: 'vertical' }}
                      data-testid="contact-message-input"
                    />
                    
                    {error && (
                      <div style={{ 
                        color: '#ff4444', 
                        fontSize: '14px', 
                        textAlign: 'center',
                        padding: '10px',
                        background: 'rgba(255,68,68,0.1)',
                        borderRadius: '8px',
                        marginTop: '8px'
                      }}>
                        {error}
                      </div>
                    )}
                    
                    <Btn 
                      variant="primary" 
                      large 
                      type="submit"
                      disabled={loading}
                      style={buttonStyle}
                      data-testid="contact-submit-btn"
                    >
                      {loading ? 'Sending...' : 'Send Message'}
                    </Btn>
                  </form>
                </Crd>
              ) : (
                <Crd glow data-testid="contact-confirmation" style={{ textAlign: 'center' }}>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    <h2 style={{ fontSize: '28px', fontWeight: 700, marginBottom: '16px', fontFamily: "'Sansation', sans-serif" }}>
                      Message Sent!
                    </h2>
                    <p className="font-[Manrope]" style={{ fontSize: '16px', lineHeight: 1.6, opacity: 0.8 }}>
                      Thanks for reaching out. We'll get back to you soon.
                    </p>
                    <button
                      onClick={() => setSubmitted(false)}
                      style={{
                        marginTop: '20px',
                        padding: '10px 20px',
                        background: 'transparent',
                        border: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)'}`,
                        borderRadius: '8px',
                        cursor: 'pointer',
                        color: theme === 'dark' ? C.white : C.black,
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'transparent';
                      }}
                    >
                      Send Another Message
                    </button>
                  </motion.div>
                </Crd>
              )}
            </div>

            <div>
              <Crd>
                <h3 style={{ fontSize: '20px', fontWeight: 700, marginBottom: '24px', fontFamily: "'Sansation', sans-serif" }}>
                  Contact Details
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  <div>
                    <div style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.1em', color: C.secondary, marginBottom: '8px' }}>EMAIL</div>
                    <a href="mailto:win@decx.ai" style={{ fontSize: '16px', color: theme === 'dark' ? C.white : C.black, textDecoration: 'none' }}>
                      win@decx.ai
                    </a>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.1em', color: C.secondary, marginBottom: '8px' }}>WHATSAPP</div>
                    <a href="https://wa.me/919987646054" style={{ fontSize: '16px', color: theme === 'dark' ? C.white : C.black, textDecoration: 'none' }}>
                      +91 9987646054
                    </a>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.1em', color: C.secondary, marginBottom: '8px' }}>WEBSITE</div>
                    <span style={{ fontSize: '16px' }}>decx.ai</span>
                  </div>
                </div>
              </Crd>
            </div>
          </div>
        </div>
      </Sec>
    </div>
  );
};